import { useEffect, useRef } from 'react'
import { useRouter } from 'next/compat/router'
import { getVariantIdCookie, trackNavigation } from '../utils/analytics'
import { AbTestingConfiguration, SelectedModuleVariants } from '../services/sanity/types'

const useTrackPageViews = (
  abTestingConfiguration?: AbTestingConfiguration,
  moduleABTestingConfiguration?: SelectedModuleVariants
): void => {
  const router = useRouter()
  const referrer = useRef<string | null>(null)

  const sendPageViewEvent = (url: string): void => {
    let pageVariant = 'default'

    if (abTestingConfiguration?.active && abTestingConfiguration?.pageVariants?.length) {
      // 1. First priority: Use selectedVariantName from server (most efficient)
      const preferredName = (abTestingConfiguration as unknown as { selectedVariantName?: string })?.selectedVariantName
      if (preferredName) {
        pageVariant = preferredName
      } else {
        const cookieName = abTestingConfiguration.cookieName || ''
        const pageTemplateId = abTestingConfiguration.pageTemplateId || ''

        // 2. Second priority: Use selectedVariantId from server (template ID)
        const preferredId = (abTestingConfiguration as unknown as { selectedVariantId?: string })?.selectedVariantId
        if (preferredId) {
          const variant = abTestingConfiguration.pageVariants.find((v) =>
            v.controlVariant ? preferredId === pageTemplateId : v.variantId === preferredId
          )
          if (variant) {
            pageVariant = variant.variantName
          }
        } else {
          // 3. Third priority: Use existing cookie value (template ID)
          const variantId = getVariantIdCookie(cookieName)
          if (variantId) {
            const variant = abTestingConfiguration.pageVariants.find((v) =>
              v.controlVariant ? variantId === pageTemplateId : v.variantId === variantId
            )
            if (variant) {
              pageVariant = variant.variantName
            }
          }
        }
      }
    }

    const pagePath: string = url
    const pageTitle: string = document.title
    const pageUrl: string = window.location.href
    const pageReferrer: string = referrer.current || document.referrer || ''

    if (pageUrl === referrer.current) return

    const navigationData: any = {
      pageVariant,
      pagePath,
      pageTitle,
      pageUrl,
      pageReferrer,
    }

    if (moduleABTestingConfiguration?.selectedNavigationVariant) {
      navigationData.navigationVariant = moduleABTestingConfiguration.selectedNavigationVariant
    }
    if (moduleABTestingConfiguration?.selectedFooterVariant) {
      navigationData.footerVariant = moduleABTestingConfiguration.selectedFooterVariant
    }

    trackNavigation(navigationData)
  }

  useEffect(() => {
    if (!router) {
      return
    }

    if (!router.isReady) {
      return
    }

    const handleRouteChange = (url: string) => {
      sendPageViewEvent(url)
      referrer.current = window.location.href
    }

    handleRouteChange(router.asPath)

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router])
}

export default useTrackPageViews
